"use client";

/**
 * Mac-Finder-style file/folder chrome used in the Computer panel.
 *
 * The folder + page glyphs themselves live in `@/components/icons/`
 * (FolderIcon3D, FolderIconYellow, FolderIcon3DOpen, PaperPageIcon) — this
 * file resolves per-type stamp colors and hosts the composite components
 * (StampBadge, DocPreviewFrame) that combine those glyphs with live content.
 */

import { cn } from "@/lib/utils";
import { PaperPageIcon } from "@/components/icons/PaperPageIcon";

/** Stamp tints — match Finder's per-type document accents:
 *  PDF → red, DOC → blue, MD → violet, JSON → amber, CODE → pink,
 *  SHEET → teal, IMAGE → emerald, OTHER → zinc.
 *
 *  Two variants per type: `bg` is the SVG `fill-*` class used by the
 *  legacy `DocIconStamped` (rect inside an svg); `htmlBg` is the
 *  matching Tailwind background class used by the new
 *  `DocPreviewFrame`, which renders the stamp as an absolute HTML
 *  badge so the page interior can host live content (text snippets,
 *  embeds, etc.) instead of fake text lines. */
export const STAMP_COLORS: Record<
  string,
  { stamp: string; bg: string; htmlBg: string }
> = {
  pdf: { stamp: "#D6342C", bg: "fill-rose-600", htmlBg: "bg-rose-600" },
  doc: { stamp: "#1F6FEB", bg: "fill-blue-600", htmlBg: "bg-blue-600" },
  md: { stamp: "#7C3AED", bg: "fill-violet-600", htmlBg: "bg-violet-600" },
  json: { stamp: "#D97706", bg: "fill-amber-600", htmlBg: "bg-amber-600" },
  code: { stamp: "#DB2777", bg: "fill-pink-600", htmlBg: "bg-pink-600" },
  sheet: { stamp: "#0D9488", bg: "fill-teal-600", htmlBg: "bg-teal-600" },
  image: { stamp: "#059669", bg: "fill-emerald-600", htmlBg: "bg-emerald-600" },
  other: { stamp: "#71717A", bg: "fill-zinc-500", htmlBg: "bg-zinc-500" },
};

/**
 * StampBadge — small coloured type label pinned to the corner of any
 * file preview. Used consistently across the Computer tile grid (image
 * thumbnails, video frames, HTML iframes, PDF embeds, text mini-pages)
 * AND the in-chat Quick Look shelf cards, so a file's "kind" reads at
 * a glance regardless of the preview content underneath.
 *
 * `position` defaults to bottom-right (Mac Finder's convention for the
 * Quick Look type tag). top-right is offered for cases where the
 * bottom edge is reserved for a filename overlay.
 */
export function StampBadge({
  filename,
  position = "bottom-right",
  size = "sm",
  className,
}: {
  filename: string;
  position?: "bottom-right" | "top-right" | "bottom-left";
  size?: "sm" | "md";
  className?: string;
}) {
  const stamp = stampForFile(filename);
  const positionClass =
    position === "bottom-right"
      ? "bottom-1 right-1"
      : position === "top-right"
        ? "top-1 right-1"
        : "bottom-1 left-1";
  const sizeClass =
    size === "md" ? "text-2.5 px-2 py-1" : "text-2.5 px-1.5 py-0.5";
  return (
    <span
      aria-hidden
      className={cn(
        "absolute pointer-events-none font-bold tracking-wider text-white rounded-4 leading-none shadow-finder-icons",
        positionClass,
        sizeClass,
        stamp.htmlBg,
        className
      )}
    >
      {stamp.label}
    </span>
  );
}

/** Derive { label, bg, htmlBg } for the document icon given an extension or category. */
export function stampForFile(filename: string): {
  label: string;
  bg: string;
  htmlBg: string;
} {
  // `lastIndexOf`, not `split('.').pop()`: an extension-less name used to
  // stamp itself, so "noextension" wore a "NOEX" badge. A leading dot still
  // counts (`.env` reads ENV) — only a name with no dot at all falls to FILE.
  const dot = filename.lastIndexOf(".");
  const ext = dot >= 0 ? filename.slice(dot + 1).toLowerCase() : "";
  if (ext === "pdf") return { label: "PDF", ...pick("pdf") };
  if (["doc", "docx", "rtf"].includes(ext))
    return { label: "DOC", ...pick("doc") };
  // Plain text keeps markdown's prose tint but wears its OWN name — a .txt
  // badged "MD" was the same lie as a .txt with no preview (founder, 08-10).
  if (["txt", "log"].includes(ext))
    return { label: ext.toUpperCase(), ...pick("md") };
  if (["md", "mdx", "markdown"].includes(ext))
    return { label: "MD", ...pick("md") };
  if (["json", "yaml", "yml", "xml"].includes(ext))
    return { label: ext.toUpperCase().slice(0, 4), ...pick("json") };
  if (
    ["py", "js", "ts", "tsx", "jsx", "sh", "sql", "html", "css"].includes(ext)
  )
    return { label: ext.toUpperCase(), ...pick("code") };
  if (["csv", "xlsx", "xls"].includes(ext))
    return { label: ext.toUpperCase(), ...pick("sheet") };
  return { label: ext.toUpperCase().slice(0, 4) || "FILE", ...pick("other") };
}

function pick(k: keyof typeof STAMP_COLORS): { bg: string; htmlBg: string } {
  const c = STAMP_COLORS[k];
  return { bg: c.bg, htmlBg: c.htmlBg };
}

// ---------------------------------------------------------------------------
// DocPreviewFrame — paper-shape that hosts live content (text snippets,
// PDF embeds, etc.) inside the page interior, with the colored type stamp
// pinned to the bottom-right corner.
//
// Why a second component (vs. extending DocIconStamped): SVG can't host
// arbitrary HTML, so the page outline is drawn as an SVG layer while the
// content sits in an absolutely-positioned HTML div on top, clipped to
// the page rectangle by inset padding. The folded corner is a second
// SVG path so the content layer can ignore that geometry.
// ---------------------------------------------------------------------------
export function DocPreviewFrame({
  stamp,
  stampHtmlBg,
  className,
  children,
}: {
  /** Short type label, e.g. "PDF", "MD", "JSON". Omit for a blank page. */
  stamp?: string;
  /** Tailwind background class for the stamp badge, e.g. "bg-rose-600". */
  stampHtmlBg?: string;
  className?: string;
  /** Content rendered inside the page rectangle. Use compact typography. */
  children?: React.ReactNode;
}) {
  return (
    <div className={cn("relative", className)}>
      {/* page outline */}
      <PaperPageIcon className="absolute inset-0 w-full h-full" />
      {/* content interior — inset matches page padding so text sits inside
          the rectangle, away from the folded corner. */}
      <div className="absolute top-[10%] left-[12%] right-[8%] bottom-[24%] overflow-hidden">
        {children}
      </div>
      {/* stamp pinned bottom-right, like Finder's PDF / DOC badges. */}
      {stamp && stampHtmlBg && (
        <div
          className={cn(
            "absolute bottom-[10%] right-[8%] px-1.5 py-0.5 rounded-4 text-2.5 font-bold text-white tracking-wide leading-none shadow-finder-icons-2",
            stampHtmlBg
          )}
        >
          {stamp}
        </div>
      )}
    </div>
  );
}
